import { useState, useEffect, useCallback } from 'react';
import { subscribeToProfileUpdates, getBestProfilePhoto } from '../utils/profileUpdateHelper';

/**
 * Custom hook for components to subscribe to profile updates
 * @param {string} eventType - Type of event to listen for ('profileDataRefreshed', 'profilePhotoUpdated', 'avatarUpdated')
 * @returns {Object} - Object containing profile data and update functions
 */
export const useProfileUpdates = (eventType = 'profileDataRefreshed') => {
    const [profileData, setProfileData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Function to load profile data from session storage
    const loadProfileData = useCallback(() => {
        try {
            const userData = sessionStorage.getItem("user");
            if (userData) {
                const user = JSON.parse(userData);
                setProfileData(user);
                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        } catch (error) {
            console.error('useProfileUpdates - Error loading profile data:', error);
            setIsLoading(false);
        }
    }, []);

    // Function to get the best profile photo
    const getBestPhoto = useCallback(() => {
        if (!profileData) return null;
        return getBestProfilePhoto(profileData);
    }, [profileData]);

    // Function to refresh profile data
    const refreshProfile = useCallback(() => {
        setIsLoading(true);
        loadProfileData();
    }, [loadProfileData]);

    useEffect(() => {
        // Load profile data initially
        loadProfileData();

        // Subscribe to profile updates
        const unsubscribe = subscribeToProfileUpdates(() => {
            console.log(`useProfileUpdates - ${eventType} event received`);
            loadProfileData();
        }, eventType);

        // Return cleanup function
        return unsubscribe;
    }, [eventType, loadProfileData]);

    return {
        profileData,
        isLoading,
        getBestPhoto,
        refreshProfile,
        // Convenience getters for common profile fields
        userName: profileData?.firstName && profileData?.lastName 
            ? `${profileData.firstName} ${profileData.lastName}` 
            : profileData?.name || profileData?.fullName || '',
        userRole: profileData?.role || '',
        avatarUrl: getBestPhoto(),
        profilePictureUrl: profileData?.profile_picture_url || '',
        email: profileData?.email || '',
        // Profile photo object for advanced usage
        profilePhotoObject: profileData?.profile_photo_url_object || null
    };
};

/**
 * Hook specifically for profile photo updates
 * @returns {Object} - Object containing profile photo data and update functions
 */
export const useProfilePhotoUpdates = () => {
    return useProfileUpdates('profilePhotoUpdated');
};

/**
 * Hook specifically for avatar updates
 * @returns {Object} - Object containing avatar data and update functions
 */
export const useAvatarUpdates = () => {
    return useProfileUpdates('avatarUpdated');
};

/**
 * Hook for general profile data updates
 * @returns {Object} - Object containing profile data and update functions
 */
export const useProfileDataUpdates = () => {
    return useProfileUpdates('profileDataRefreshed');
};
